/**
 * Validation for host-page metadata payloads sent via `pypeLoader.sendMetadata`
 * / `pypeLoader.registerForEvents`. The backend contract requires `data` to be a
 * JSON object or array; we additionally bound depth and stringified size
 * so a misbehaving page can't poison the bot's `metadata.events` with a
 * huge or deeply-nested payload.
 *
 * Deliberately dependency-free and duplicated from the widget's
 * `apps/pype/src/common/schemas/webview-passthrough.ts` rather than
 * imported: pulling anything from `@pypestream/pype` into the pypeLoader bundle
 * would drag XState/Lit across the pypeLoader boundary (enforced by the
 * `no-restricted-imports` ESLint rule). ~30 LOC of pure functions is the
 * accepted cost of keeping the boundary clean.
 */

/** Max nesting depth of objects/arrays. Anything deeper is rejected. */
export const MAX_METADATA_DEPTH = 5;

/** Max stringified JSON size (bytes). Anything larger is rejected. */
export const MAX_METADATA_STRINGIFIED_BYTES = 32 * 1024;

/** Reasons a payload can fail validation — surfaced in errors/warnings. */
export type MetadataInvalidReason =
  | 'not-object-or-array'
  | 'too-deep'
  | 'too-large';

export type MetadataValidationResult =
  | { ok: true }
  | { ok: false; reason: MetadataInvalidReason };

/** True if `value` is a plain object or array (not a primitive/null). */
function isObjectOrArray(
  value: unknown
): value is Record<string, unknown> | unknown[] {
  return value !== null && typeof value === 'object';
}

/** Depth-cap walker — true if any nested level exceeds `limit`. */
export function exceedsDepth(
  value: unknown,
  limit: number = MAX_METADATA_DEPTH,
  depth = 0
): boolean {
  if (depth > limit) return true;
  if (value === null || typeof value !== 'object') return false;
  if (Array.isArray(value)) {
    return value.some((v) => exceedsDepth(v, limit, depth + 1));
  }
  return Object.values(value as Record<string, unknown>).some((v) =>
    exceedsDepth(v, limit, depth + 1)
  );
}

/** Size-cap check on the stringified form. */
export function exceedsStringifiedSize(
  value: unknown,
  limit: number = MAX_METADATA_STRINGIFIED_BYTES
): boolean {
  try {
    return JSON.stringify(value).length > limit;
  } catch {
    // Circular ref / non-serializable — treat as oversized so the caller
    // rejects it rather than forwarding something the wire can't carry.
    return true;
  }
}

/**
 * Validate a metadata payload against the backend contract + safety caps.
 * Pure — no side effects; the caller decides whether to throw or warn.
 */
export function validateMetadata(data: unknown): MetadataValidationResult {
  if (!isObjectOrArray(data))
    return { ok: false, reason: 'not-object-or-array' };
  if (exceedsDepth(data)) return { ok: false, reason: 'too-deep' };
  if (exceedsStringifiedSize(data)) return { ok: false, reason: 'too-large' };
  return { ok: true };
}
